import './OrderDetails.scss';

import { type FC, useMemo } from 'react';
import { Translation, Trans } from 'react-i18next';
import { useFormikContext } from 'formik';
import { uniq } from 'lodash-es';

import { FontLicenseType } from 'client/graphql/types/operations';

import * as Core from 'components/core';
import * as Braintree from 'components/braintree';
import PayPalButton from './PayPalButton';
import PromotionCodeInput from './PromotionCodeInput';
import LicenseCheckboxes from './LicenseCheckboxes';

import vatCountryCode from 'utils/vatCountryCode';
import formatCurrency from 'utils/formatCurrency';

import { PaymentMethod, PaymentAuthorization } from 'types';
import { Values } from './types';

import useCountriesQuery from 'queries/useCountriesQuery';

interface Props {
  order: {
    subtotal: number;
    adjustmentsTotal: number;
    fontLicenses: { licenseOption: { type: FontLicenseType } }[];
    adjustments: { id: number; amount: number; description: string }[];
  };
  paymentMethod: PaymentMethod;
  billingCountryCode?: string;
  vatNumber?: string;
  disabled: boolean;
  paypalDisabled: boolean;
  onPayPalAuthorize(authorization: PaymentAuthorization): void;
  onPayPalError(error: string): void;
  onApplyPromotion(): void;
  onLicensesAgreedToChange(value: boolean): void;
  onTermsAndConditionsAgreedToChanged(value: boolean): void;
}

const CheckoutOrderDetails: FC<Props> = (props) => {
  const {
    order,
    paymentMethod,
    billingCountryCode,
    vatNumber,
    disabled,
    paypalDisabled,
    onPayPalAuthorize,
    onPayPalError,
    onApplyPromotion,
  } = props;

  const braintreeEnvironment = Braintree.useBraintreeEnvironment();
  const paypalClient = Braintree.usePaypalClient();

  const countriesQuery = useCountriesQuery();

  const billingCountryAlpha2 = useMemo(() => {
    if (countriesQuery.data == null) {
      return null;
    }

    const country = countriesQuery.data.countries.find(
      (country) => country.alpha3 === billingCountryCode,
    );

    return country?.alpha2;
  }, [countriesQuery.data, billingCountryCode]);

  const { values, errors, handleChange, handleBlur } =
    useFormikContext<Values>();

  const { licenseeIsLegalEntity } = values;

  const taxTotal = useMemo(() => {
    // Calculate the total and taxTotal client-side so that we don't have to
    // make a request when the billing country changes.
    if (
      licenseeIsLegalEntity === false ||
      billingCountryCode === 'DEU' ||
      ((vatNumber == null || vatNumber.length === 0) &&
        billingCountryCode != null &&
        vatCountryCode(billingCountryCode))
    ) {
      return window.ENV.vat.taxRate * (order.subtotal + order.adjustmentsTotal);
    } else {
      return 0;
    }
  }, [
    licenseeIsLegalEntity,
    billingCountryCode,
    vatNumber,
    order.subtotal,
    order.adjustmentsTotal,
  ]);

  const total: number = order.subtotal + order.adjustmentsTotal + taxTotal;

  return (
    <Translation ns={['translation', 'checkout']}>
      {(t) => (
        <>
          {values.licenseeIsLegalEntity &&
            billingCountryCode != null &&
            vatCountryCode(billingCountryCode) && (
              <Core.InputFormGroup
                name="vatNumber"
                label={t('payment.vatNumber', { ns: 'checkout' })}
                placeholder={
                  billingCountryAlpha2 != null
                    ? `${billingCountryAlpha2}*********`
                    : undefined
                }
                disabled={disabled}
                required
                errorMessage={errors.vatNumber}
                testID="vat-number-field"
                onChange={handleChange}
                onBlur={handleBlur}
              />
            )}

          <PromotionCodeInput
            value={values.promotionCode}
            disabled={disabled}
            errorMessage={errors.promotionCode}
            onApply={onApplyPromotion}
            onChange={handleChange}
            onBlur={handleBlur}
          />

          <div className="CheckoutOrderDetails-table">
            <div className="CheckoutOrderDetails-table-row">
              <div className="CheckoutOrderDetails-table-row-col">
                {t('order.subtotal')}
              </div>

              <div className="CheckoutOrderDetails-table-row-col">
                {formatCurrency(order.subtotal)}
              </div>
            </div>

            {order.adjustments.map((adjustment) => (
              <div
                key={adjustment.id}
                className="CheckoutOrderDetails-table-row"
              >
                <div className="CheckoutOrderDetails-table-row-col">
                  {adjustment.description}
                </div>

                <div className="CheckoutOrderDetails-table-row-col">
                  {formatCurrency(adjustment.amount)}
                </div>
              </div>
            ))}

            {taxTotal > 0 && (
              <div className="CheckoutOrderDetails-table-row">
                <div className="CheckoutOrderDetails-table-row-col">
                  {t('order.taxTotal')}
                </div>

                <div className="CheckoutOrderDetails-table-row-col">
                  {formatCurrency(taxTotal)}
                </div>
              </div>
            )}

            <div className="CheckoutOrderDetails-table-row">
              <div className="CheckoutOrderDetails-table-row-col">
                {t('order.total')}
              </div>

              <div className="CheckoutOrderDetails-table-row-col">
                {formatCurrency(total)}
              </div>
            </div>
          </div>

          <div className="CheckoutOrderDetails-checkboxes">
            <LicenseCheckboxes
              licenseTypes={uniq(
                order.fontLicenses.map(
                  ({ licenseOption }) => licenseOption.type,
                ),
              )}
              onChange={props.onLicensesAgreedToChange}
            />

            <Core.Checkbox
              disabled={disabled}
              onChange={(event) =>
                props.onTermsAndConditionsAgreedToChanged(
                  event.currentTarget.checked,
                )
              }
              testID="terms-and-conditions-checkbox"
            >
              <Trans ns="checkout" i18nKey="payment.agreeToTermsAndConditions">
                <Core.Link to="/terms-and-conditions" target="_blank">
                  {t('pages.termsAndConditions')}
                </Core.Link>
              </Trans>
            </Core.Checkbox>

            <Core.Checkbox
              name="receiveNewsletter"
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              {t('payment.newsletterOptIn', { ns: 'checkout' })}
            </Core.Checkbox>
          </div>

          {paymentMethod === 'paypal' && (
            <PayPalButton
              environment={braintreeEnvironment}
              paypalCheckout={paypalClient}
              paymentAmount={total}
              disabled={paypalDisabled}
              onAuthorize={onPayPalAuthorize}
              onError={onPayPalError}
            />
          )}
        </>
      )}
    </Translation>
  );
};

export default CheckoutOrderDetails;
